import {
  ArrowPathIcon,
  DocumentTextIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  useGetTaskRunsQuery,
  useRefreshEnhancedBlandCallWorkflowStatusMutation,
  useRetryEnhancedBlandCallWorkflowMutation,
  useTerminateEnhancedBlandCallWorkflowMutation,
} from '@/services/api/blandService';
import { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskType } from '@/services/types/task-types';
import {
  EnhancedBlandWorkflowState,
  EnhancedBlandWorkflowStatus,
  VoiceAIAgentRunSchema,
} from '@/types/enhanced-bland-call';

import { RunDetailView, RunHistoryList } from '../drawers/RunHistory';

// Shim to convert workflow state to a run schema for consistent display
const workflowStateToRunSchema = (
  workflow: EnhancedBlandWorkflowState,
): VoiceAIAgentRunSchema => ({
  id: workflow.workflow_id, // Using workflow_id as a unique key
  workflow_id: workflow.workflow_id,
  status: workflow.status,
  calls_made: workflow.call_results.length,
  max_calls_allowed: workflow.max_calls_allowed,
  summary: workflow.last_message,
  event_log: workflow.event_log,
  messages: [], // Current workflow state doesn't have detailed messages
  error_message: workflow.error_message || undefined,
  created_at: workflow.created_at,
  updated_at: workflow.updated_at,
  call_results: workflow.call_results,
});

interface VoiceAgentRunModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task;
  matterId: string;
  workflow: EnhancedBlandWorkflowState | null;
  onRefetch: () => void;
  onStartWorkflow?: () => Promise<void>;
  isStartingWorkflow?: boolean;
}

export const VoiceAgentRunModal: React.FC<VoiceAgentRunModalProps> = ({
  isOpen,
  onClose,
  task,
  matterId,
  workflow,
  onRefetch,
  onStartWorkflow,
  isStartingWorkflow = false,
}) => {
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [selectedRun, setSelectedRun] = useState<VoiceAIAgentRunSchema | null>(
    null,
  );
  const [retryWorkflow, { isLoading: retryLoading }] =
    useRetryEnhancedBlandCallWorkflowMutation();
  const [terminateWorkflow, { isLoading: terminateLoading }] =
    useTerminateEnhancedBlandCallWorkflowMutation();
  const [refreshWorkflowStatus, { isLoading: refreshLoading }] =
    useRefreshEnhancedBlandCallWorkflowStatusMutation();

  const { data: runsData } = useGetTaskRunsQuery(
    { matterId, taskId: task.id },
    { skip: !isOpen },
  );

  const [lastWorkflowStatus, setLastWorkflowStatus] = useState(
    workflow?.status,
  );

  useEffect(() => {
    if (workflow?.status !== lastWorkflowStatus && isOpen) {
      const statusMessages: Record<EnhancedBlandWorkflowStatus, string> = {
        [EnhancedBlandWorkflowStatus.SCHEDULED]: '📅 Workflow scheduled',
        [EnhancedBlandWorkflowStatus.STARTED]: '🚦 Workflow starting...',
        [EnhancedBlandWorkflowStatus.FAILED_READY_CHECK]:
          '⚠️ Missing information',
        [EnhancedBlandWorkflowStatus.THINKING]: '🤔 Agent thinking...',
        [EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS]: '📞 Call in progress!',
        [EnhancedBlandWorkflowStatus.WAITING]: '😴 Agent waiting...',
        [EnhancedBlandWorkflowStatus.COMPLETED]: '🏁 Workflow completed!',
        [EnhancedBlandWorkflowStatus.FAILED]: '❌ Workflow failed',
        [EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE]: '❌ Task ineligible',
        [EnhancedBlandWorkflowStatus.CANCELLED]: '🚫 Workflow cancelled',
      };

      const message = workflow ? statusMessages[workflow.status] : null;
      if (message && workflow) {
        if (workflow.status === EnhancedBlandWorkflowStatus.COMPLETED) {
          toast.success(message);
        } else if (workflow.status === EnhancedBlandWorkflowStatus.FAILED) {
          toast.error(message);
        } else {
          toast.info(message);
        }
        setLastWorkflowStatus(workflow.status);
      }
    }
  }, [workflow?.status, lastWorkflowStatus, isOpen]);

  useEffect(() => {
    if (!isOpen || activeTab === 'current') {
      setSelectedRun(null);
    }
  }, [isOpen, activeTab]);

  if (!isOpen) return null;

  const getTaskDisplayInfo = () => {
    switch (task.type.canonical_type) {
      case CanonicalTaskType.OPEN_CLAIM: {
        const { coverage } = task;
        return {
          title: 'Insurance Claim Call',
          subtitle: `AI calling ${
            coverage?.carrier?.name || 'insurance carrier'
          }`,
          icon: PhoneIcon,
        };
      }
      case CanonicalTaskType.POLICE_REPORT:
        return {
          title: 'Police Report Request',
          subtitle: 'AI calling police department',
          icon: DocumentTextIcon,
        };
      default:
        return {
          title: 'AI Voice Agent',
          subtitle: 'Automated phone call workflow',
          icon: PhoneIcon,
        };
    }
  };

  const displayInfo = getTaskDisplayInfo();

  const handleRetryWorkflow = async () => {
    try {
      await retryWorkflow({ matterId, taskId: task.id }).unwrap();
      toast.success('Status updated!');
      onRefetch();
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to update workflow');
    }
  };

  const handleTerminateWorkflow = async () => {
    if (!window.confirm('Terminate this workflow? This cannot be undone.'))
      return;

    try {
      await terminateWorkflow({ matterId, taskId: task.id }).unwrap();
      toast.success('Workflow terminated');
      onRefetch();
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to terminate workflow');
    }
  };

  const handleRefreshStatus = async () => {
    try {
      await refreshWorkflowStatus({ matterId, taskId: task.id }).unwrap();
      toast.success('Status refreshed!');
      onRefetch();
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to refresh status');
    }
  };

  const canRetry = false; // Removed retry functionality - FAILED_READY_CHECK is now terminal
  const canTerminate = ![
    EnhancedBlandWorkflowStatus.COMPLETED,
    EnhancedBlandWorkflowStatus.FAILED,
    EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE,
    EnhancedBlandWorkflowStatus.FAILED_READY_CHECK, // Now treated as terminal
    EnhancedBlandWorkflowStatus.CANCELLED,
  ].includes(workflow?.status || EnhancedBlandWorkflowStatus.STARTED);

  const canStartNewRun =
    onStartWorkflow &&
    (!workflow ||
      [
        EnhancedBlandWorkflowStatus.COMPLETED,
        EnhancedBlandWorkflowStatus.FAILED,
        EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE,
        EnhancedBlandWorkflowStatus.FAILED_READY_CHECK, // Now included as terminal state
        EnhancedBlandWorkflowStatus.CANCELLED,
      ].includes(workflow.status));

  const renderCurrentRun = () => {
    if (!workflow) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <displayInfo.icon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Active Workflow
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Start an AI workflow to {displayInfo.subtitle.toLowerCase()}
          </p>
          {onStartWorkflow && (
            <button
              type="button"
              onClick={onStartWorkflow}
              disabled={isStartingWorkflow}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 mx-auto"
            >
              {isStartingWorkflow ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
              ) : (
                <displayInfo.icon className="h-5 w-5" />
              )}
              {isStartingWorkflow ? 'Starting...' : 'Start AI Workflow'}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {workflow.status === EnhancedBlandWorkflowStatus.FAILED_READY_CHECK &&
          workflow.readiness?.missing_items.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-900 mb-2">
                ⚠️ Missing Information
              </h4>
              <ul className="space-y-1">
                {workflow.readiness.missing_items.map((item, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-amber-800 flex items-start"
                  >
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        <RunDetailView run={workflowStateToRunSchema(workflow)} isCurrentRun />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <displayInfo.icon className="h-6 w-6 text-gray-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {displayInfo.title}
              </h2>
              <p className="text-sm text-gray-500">{displayInfo.subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              type="button"
              onClick={() => setActiveTab('current')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'current'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Current Run
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Run History{' '}
              {runsData && runsData.count > 0 && `(${runsData.count})`}
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'current' && renderCurrentRun()}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {selectedRun ? (
                <RunDetailView
                  run={selectedRun}
                  onBack={() => setSelectedRun(null)}
                />
              ) : !runsData || runsData.runs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Run History
                  </h3>
                  <p className="text-sm text-gray-600">
                    Previous AI workflow runs will appear here
                  </p>
                </div>
              ) : (
                <RunHistoryList
                  runs={runsData.runs}
                  onSelectRun={setSelectedRun}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleRefreshStatus}
              disabled={refreshLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {refreshLoading ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowPathIcon className="h-4 w-4" />
              )}
              Refresh
            </button>

            {canStartNewRun && (
              <button
                type="button"
                onClick={onStartWorkflow}
                disabled={isStartingWorkflow}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isStartingWorkflow ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <displayInfo.icon className="h-4 w-4" />
                )}
                Start New Run
              </button>
            )}

            {canTerminate && (
              <button
                type="button"
                onClick={handleTerminateWorkflow}
                disabled={terminateLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font--medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {terminateLoading ? (
                  <XMarkIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <XMarkIcon className="h-4 w-4" />
                )}
                Terminate
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
